import React, { useState } from "react";
import clsx from "clsx";
import { Button } from "../common/Button";
import FeedbackPrompt from "./FeedbackPrompt"; 
import { ParentOrder, OrderStatus, SelectedCustomization } from "../../types/index";
import { CartItem } from "../../types/index";
import { toast } from "react-hot-toast";
import { reportIssueWithOrder, cancelOrder } from "../../services/order.service";
import { useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "../../store/user.store";
import { Card } from "../common/Card";
import { OrderModificationModal } from "./OrderModificationModal"; 
import { Link } from "react-router-dom";
import { trackPath } from "../../lib/orderTracking";

// Helper to check if the order is in the "cooling off" period
const isWithinCoolingOff = (deliveredAt: any): boolean => {
  if (!deliveredAt || !deliveredAt.toDate) return false;
  const deliveryTime = deliveredAt.toDate().getTime();
  const now = new Date().getTime();
  const twoHours = 2 * 60 * 60 * 1000;
  return (now - deliveryTime) < twoHours;
};

// Helper to check if order is cancellable
const isCancellable = (status: OrderStatus): boolean => {
  return ![
    "out_for_delivery",
    "delivered",
    "settled",
    "cancelled",
    "complaint_raised",
  ].includes(status);
};

// Helper to group items by kitchen
const groupItemsByKitchen = (items: CartItem[]) => {
  return items.reduce((acc, item) => {
    const { kitchenId, kitchenName } = item;
    const safeKitchenId = kitchenId || "unknown";
    const safeKitchenName = kitchenName || "Unknown Kitchen";

    if (!acc[safeKitchenId]) {
      acc[safeKitchenId] = {
        kitchenName: safeKitchenName,
        items: [],
      };
    }
    acc[safeKitchenId].items.push(item);
    return acc;
  }, {} as Record<string, { kitchenName: string; items: CartItem[] }>);
};

// ✅ Robust date formatter
const formatOrderDate = (createdAt: any): string => {
  try {
    if (!createdAt) return 'Date not available';
    
    let date: Date;
    if (createdAt.toDate) {
      date = createdAt.toDate();
    } else if (typeof createdAt === 'number') {
      date = new Date(createdAt * 1000);
    } else {
      date = new Date(createdAt);
    }
    
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Date not available';
  }
};

// ✅ HELPER: Extract Variant & Addons safely
const getCustomizationDetails = (customizations: any) => {
  if (!customizations) return { variant: null, addons: [] };

  let variant = null;
  let addons: string[] = [];

  // 1. New Array Structure
  if (Array.isArray(customizations)) {
    const variantItem = customizations.find(
      (c: any) => c.label === "Variant" || c.label === "Size" || c.type === "variant"
    );
    if (variantItem) variant = variantItem.value;

    addons = customizations
      .filter((c: any) => c.type === "addon" || c.label.toLowerCase() === "addons")
      .map((c: any) => c.value);
  } 
  // 2. Old Object Structure
  else {
    variant = customizations.variant || null;
    if (customizations.addons && Array.isArray(customizations.addons)) {
      addons = customizations.addons;
    }
  }

  return { variant, addons };
};

type OrderCardProps = {
  order: ParentOrder;
  className?: string;
  isAdmin?: boolean; 
};

export const OrderCard: React.FC<OrderCardProps> = ({ order, className, isAdmin = false }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false); 
  const queryClient = useQueryClient();
  const { userProfile } = useUserStore();
  
  const inCoolingOff = isWithinCoolingOff(order.deliveredAt);
  const itemsByKitchen = groupItemsByKitchen(order.items);
  const canCancel = isCancellable(order.status);

  const handleFeedbackSubmit = (data: { rating: number; comment: string }) => {
    console.log("Feedback submitted for order:", order.id, data.rating, data.comment);
    setShowFeedback(false);
  };

  const handleReportIssue = async (kitchenOrderId: string) => {
    const reason = prompt(
      "Please describe the issue with this kitchen's order (min 10 chars):"
    );
    if (!reason || reason.trim().length < 10) {
      toast.error("Please provide a detailed reason.");
      return;
    }

    setIsReporting(true);
    try {
      await reportIssueWithOrder(
        order.id,
        `kitchen_${kitchenOrderId}`,
        reason
      );
      toast.success("Issue reported. Admin will review.");
      queryClient.invalidateQueries(["orderHistory", userProfile?.uid]);
    } catch (error) {
      toast.error((error as Error).message);
    }
    setIsReporting(false);
  };

  const handleCancelOrder = async () => {
    if (!window.confirm("Are you sure you want to cancel this entire order? Wallet payments are refunded to your wallet.")) {
      return;
    }
    setIsCancelling(true);
    try {
      const res = await cancelOrder(order.id);
      toast.success(res?.message || "Order cancelled.");
      queryClient.invalidateQueries(["orderHistory", userProfile?.uid]);
    } catch (error) {
      toast.error((error as Error).message);
    }
    setIsCancelling(false);
  };

  return (
    <>
      <div className={clsx("bg-white rounded-xl shadow-lg p-6 border border-gray-100 mb-6", className)}>
        {/* Header */}
        <div className="flex justify-between items-start border-b border-gray-200 pb-4 mb-6">
          <div className="flex-1">
            <h4 className="text-xl font-bold text-gray-900 mb-1">
              Order #{order.id.substring(0, 8).toUpperCase()}
            </h4>
            <p className="text-sm text-gray-500 font-medium">
              {formatOrderDate(order.createdAt)}
            </p>
          </div>
          <div className="text-right ml-4">
            <div className="text-2xl font-bold text-indigo-600 mb-1">
              ₹{order.totalAmount.toFixed(2)}
            </div>
            <div className="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-full inline-block">
              {order.status === "awaiting_payment" ? "WAITING FOR PAYMENT" : order.status.replace(/_/g, " ").toUpperCase()}
            </div>
          </div>
        </div>
        
        {/* Status Banners */}
        {Array.isArray((order as any).awaitingChefKitchenIds) && (order as any).awaitingChefKitchenIds.length > 0 && order.status !== "cancelled" && (
          <Card className="bg-amber-50 border-amber-200 mb-6 p-4 rounded-xl">
            <p className="text-sm text-amber-800">
              <strong>Past cut-off, asking the chef.</strong> Part of this order was placed after the kitchen&apos;s cut-off;
              the chef has about 15 minutes to confirm. If they can&apos;t, that part is cancelled and any amount paid goes back to your wallet.
            </p>
          </Card>
        )}
        {Array.isArray((order as any).cancelledKitchenIds) && (order as any).cancelledKitchenIds.length > 0 && order.status !== "cancelled" && (
          <Card className="bg-gray-50 border-gray-200 mb-6 p-4 rounded-xl">
            <p className="text-sm text-gray-700">
              One kitchen couldn&apos;t take its part after the cut-off, so it was cancelled
              {(order as any).partialRefundAmount ? ` and ₹${Number((order as any).partialRefundAmount).toFixed(2)} was refunded to your wallet` : ""}.
            </p>
          </Card>
        )}
        {order.status === "delivered" && !order.hasComplaint && inCoolingOff && (
          <Card className="bg-yellow-50 border-yellow-200 mb-6 p-4 rounded-xl">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              <p className="text-sm text-yellow-800 font-medium">
                Delivered recently - You have 2 hours to report issues
              </p>
            </div>
          </Card>
        )}
        
        {order.hasComplaint && (
          <Card className="bg-red-50 border-red-200 mb-6 p-4 rounded-xl">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <p className="font-semibold text-red-700">
                Complaint raised - Admin review in progress
              </p>
            </div>
          </Card>
        )}

        {/* Items List */}
        <div className="mb-6">
          <h5 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h5>
          <div className="space-y-4">
            {Object.entries(itemsByKitchen).map(([kitchenId, group], idx) => (
              <div key={kitchenId} className={clsx("p-4 rounded-xl border", 
                idx > 0 ? "border-gray-200 bg-gray-50/50" : "border-indigo-100 bg-indigo-50/30"
              )}>
                <h6 className="font-bold text-indigo-700 text-lg mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                  {group.kitchenName}
                </h6>
                <ul className="space-y-2 mb-4">
                  {group.items.map((item) => {
                    const { variant, addons } = getCustomizationDetails(item.customizations);
                    
                    return (
                      <li key={item.id} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                        <div className="flex-1">
                          <span className="font-medium text-gray-900">
                            {item.name} <span className="text-sm font-normal">(x{item.quantity})</span>
                          </span>
                          
                          {/* ✅ SHOW VARIANT AND ADDONS */}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {variant && (
                              <span className="px-2 py-0.5 bg-gray-100 text-[10px] uppercase font-bold text-gray-600 rounded">
                                {variant}
                              </span>
                            )}
                            {addons.length > 0 && addons.map((addon, i) => (
                              <span key={i} className="px-2 py-0.5 bg-green-50 text-[10px] font-bold text-green-700 rounded border border-green-100">
                                + {addon}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span className="font-bold text-indigo-600 text-right ml-2">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                
                {/* Report Issue Button */}
                {order.status === "delivered" && !order.hasComplaint && inCoolingOff && (
                  <Button 
                    onClick={() => {
                      const kOrderId = order.kitchenOrderIds && order.kitchenOrderIds[idx];
                      if (kOrderId) {
                        handleReportIssue(kOrderId);
                      } else {
                        toast.error("Could not identify kitchen order ID.");
                      }
                    }} 
                    variant="danger"
                    size="sm"
                    isLoading={isReporting}
                    className="w-full"
                  >
                    Report Issue → {group.kitchenName}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
          {order.status !== "cancelled" && (
            <Link
              to={trackPath(order as any)}
              className="flex-1 text-center bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg"
            >
              📍 Track order
            </Link>
          )}
          {order.status === "settled" && !order.feedbackSubmitted && (
            <Button 
              onClick={() => setShowFeedback(true)} 
              variant="primary" 
              className="flex-1"
            >
              ⭐ Give Feedback
            </Button>
          )}
          
          {/* ✅ ADMIN EDIT BUTTON */}
          {isAdmin && (
            <Button 
              onClick={() => setShowModifyModal(true)}
              variant="secondary"
              className="flex-1"
            >
              ✏️ Edit Items
            </Button>
          )}
          
          {canCancel && (
            <Button 
              onClick={handleCancelOrder}
              variant="danger"
              size="lg"
              isLoading={isCancelling}
              className="flex-1 font-semibold"
            >
              Cancel Order
            </Button>
          )}
        </div>

        {/* Feedback Modal */}
        {showFeedback && (
          <FeedbackPrompt
            orderId={order.id}
            isOpen={showFeedback}
            onClose={() => setShowFeedback(false)}
            onSubmit={handleFeedbackSubmit}
          />
        )}
      </div>

      {/* ✅ ORDER MODIFICATION MODAL */}
      {isAdmin && showModifyModal && (
        <OrderModificationModal
          isOpen={showModifyModal}
          onClose={() => setShowModifyModal(false)}
          orderId={order.id}
          userId={order.customerId}
          originalItems={order.items}
          originalTotal={order.totalAmount}
          onSuccess={() => {
            toast.success("Order updated successfully!");
            queryClient.invalidateQueries(["adminAllOrders"]);
          }}
        />
      )}
    </>
  );
};